/**
 * ReauthBanner
 *
 * Sticky global banner that warns the user when one or more workspace
 * integrations require re-authorization (new API scopes available).
 * Rendered once at the top of the authenticated app layout — only visible
 * when `useReauthStatus().needsReauth === true`.
 *
 * Click on the CTA opens the workspace settings integrations tab so the
 * user can re-authorize from the existing integration card flow.
 *
 * A11y: role="alert" + aria-live="polite". Hidden entirely when no reauth
 * is required (no extra noise for healthy workspaces).
 */

import { useNavigate } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui/Button'
import { useReauthStatus } from '@/hooks/useReauthStatus'

export function ReauthBanner() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { needsReauth, integrations } = useReauthStatus()

  if (!needsReauth) return null

  const count = integrations.length || 1
  // i18next picks `reauth.banner.message_one` / `_other` automatically based on count.
  const message = t('reauth.banner.message', { count })

  const handleClick = () => {
    navigate('/v2/workspace-settings?tab=integrations')
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="sticky top-16 z-40 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <ShieldAlert
            className="h-5 w-5 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden="true"
          />
          <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
            {message}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={handleClick}
        >
          {t('reauth.banner.cta')}
        </Button>
      </div>
    </div>
  )
}

export default ReauthBanner
